import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {API_BASE_URL} from "../config";
import ApplicationModal from "../components/ApplicationModal";



const Careers = () => {
    const { fetchWithAuth } = useAuth();
    const [apiJobs, setApiJobs] = useState([]);
    const [categories, setCategories] = useState(["All"]);
    const [locations, setLocations] = useState(["All"]);
    const [employmentTypes, setEmploymentTypes] = useState(["All"]);
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedLocation, setSelectedLocation] = useState("All");
    const [selectedEmploymentType, setSelectedEmploymentType] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);

    // Fetch jobs with filters and pagination
    const fetchJobs = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('per_page', 3);

            if (selectedCategory && selectedCategory !== "All") {
                params.append('category', selectedCategory);
            }

            if (selectedLocation && selectedLocation !== "All") {
                params.append('location', selectedLocation);
            }

            if (selectedEmploymentType && selectedEmploymentType !== "All") {
                params.append('employment_type', selectedEmploymentType);
            }

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            console.log("Fetching jobs from /api/careers...");
            const jobsFromApi = await fetchWithAuth(`/api/careers?${params.toString()}`);
            console.log("Jobs response:", jobsFromApi);

            // API returns paginated data with jobs in the 'data' property
            setApiJobs(jobsFromApi?.data || []);
            setCurrentPage(jobsFromApi?.current_page || 1);
            setLastPage(jobsFromApi?.last_page || 1);
            setTotal(jobsFromApi?.total || 0);
        } catch (err) {
            console.error("Failed to fetch careers data:", err);
            setError(err.message || "Failed to load jobs. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch filter options (categories and locations)
    const fetchFilterOptions = async () => {
        try {
            const categoriesFromApi = await fetchWithAuth(`/api/careers/categories`);
            if (categoriesFromApi && Array.isArray(categoriesFromApi)) {
                // Extract category names from the API response
                const categoryNames = categoriesFromApi.map(cat => cat.name);
                setCategories(["All", ...categoryNames]);
            }

            console.log("Fetching locations from /api/careers/locations...");
            const locationsFromApi = await fetchWithAuth(`/api/careers/locations`);
            console.log("Locations response:", locationsFromApi);
            if (locationsFromApi && Array.isArray(locationsFromApi)) {
                // Handle both array of strings or array of objects
                const locationNames = locationsFromApi.map(loc =>
                    typeof loc === 'string' ? loc : loc.name
                );
                setLocations(["All", ...locationNames]);
            }

            // Set employment types (hardcoded as these are typically standard)
            setEmploymentTypes(["All", "Full-time", "Part-time", "Contract", "Temporary"]);
        } catch (err) {
            console.error("Failed to fetch filter options:", err);
        }
    };

    // Fetch filter options on mount
    useEffect(() => {
        fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch jobs when filters or page changes
    useEffect(() => {
        fetchJobs(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, selectedLocation, selectedEmploymentType, searchQuery, currentPage]);

    // Use jobs from API
    const allJobs = apiJobs;

    // Check if user has applied to a job
    const hasAppliedToJob = (jobId) => {
        const appliedJobs = JSON.parse(localStorage.getItem('appliedJobs') || '[]');
        return appliedJobs.includes(jobId);
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleLocationChange = (e) => {
        setSelectedLocation(e.target.value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleEmploymentTypeChange = (e) => {
        setSelectedEmploymentType(e.target.value);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };


    return (
        <div className="bg-[#F2F1F0] text-[#144E73] font-sans min-h-screen pt-16 pb-20 px-6">
            <div className="max-w-5xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold text-primarymb-6 text-center">
                    Join the Benecke Group
                </h1>
                <p className="text-center text-lg text-gray-600 mb-8">
                    Explore exciting career opportunities in hospitality and property management.
                </p>

                {/* Error Message */}
                {error && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{error}</p>
                        <button
                            onClick={() => fetchJobs(currentPage)}
                            className="mt-2 text-red-700 underline hover:text-red-900"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* Search and Filters Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    {/* Search Bar */}
                    <form onSubmit={handleSearchSubmit} className="mb-4">
                        <div className="relative">
                            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search jobs by title, description, or duties..."
                                className="w-full pl-12 pr-32 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1D4E80] focus:ring-2 focus:ring-[#1D4E80]/20"
                                disabled={loading}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={handleClearSearch}
                                        className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition"
                                        disabled={loading}
                                    >
                                        Clear
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="px-5 py-1.5 bg-primary text-white rounded-md hover:bg-[#144E73] transition text-sm font-medium disabled:opacity-50"
                                    disabled={loading}
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </form>

                    {/* Filters Row */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Filter by:</span>
                            {total > 0 && (
                                <span className="text-sm text-gray-600 ml-auto">
                                    <span className="font-semibold text-[#1D4E80]">{total}</span> {total === 1 ? 'job' : 'jobs'} found
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <select
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1D4E80]"
                                disabled={loading}
                            >
                                {categories.map((cat, i) => (
                                    <option key={i} value={cat}>
                                        {cat === "All" ? "All Categories" : cat}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedLocation}
                                onChange={handleLocationChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1D4E80]"
                                disabled={loading}
                            >
                                {locations.map((loc, i) => (
                                    <option key={i} value={loc}>
                                        {loc === "All" ? "All Locations" : loc}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedEmploymentType}
                                onChange={handleEmploymentTypeChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1D4E80]"
                                disabled={loading}
                            >
                                {employmentTypes.map((type, i) => (
                                    <option key={i} value={type === "All" ? type : type.toLowerCase().replace('-', '_')}>
                                        {type === "All" ? "All Types" : type}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1D4E80]"></div>
                        <p className="text-gray-600 mt-4">Loading career opportunities...</p>
                    </div>
                ) : allJobs.length ? (
                    <>
                    <div className="space-y-4">
                        {allJobs.map((job, index) => {
                            const isExpanded = expandedJobId === job.id || expandedJobId === index;
                            const jobKey = job.id || index;

                            return (
                                <div
                                    key={index}
                                    className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                                        <div className="flex-1">
                                            {/* Job Title */}
                                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{job.title}</h2>

                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {/* Employment Type */}
                                                {job.employment_type && (
                                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                        job.employment_type === 'full_time'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : job.employment_type === 'part_time'
                                                            ? 'bg-indigo-100 text-indigo-700'
                                                            : job.employment_type === 'contract'
                                                            ? 'bg-cyan-100 text-cyan-700'
                                                            : job.employment_type === 'temporary'
                                                            ? 'bg-teal-100 text-teal-700'
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {job.employment_type === 'full_time'
                                                            ? 'Full-time'
                                                            : job.employment_type === 'part_time'
                                                            ? 'Part-time'
                                                            : job.employment_type === 'contract'
                                                            ? 'Contract'
                                                            : job.employment_type === 'temporary'
                                                            ? 'Temporary'
                                                            : job.employment_type.charAt(0).toUpperCase() + job.employment_type.slice(1).replace('_', ' ')}
                                                    </span>
                                                )}
                                                {/* Remote */}
                                                {job.location?.toLowerCase().includes('remote') && (
                                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                                        Remote
                                                    </span>
                                                )}
                                                {/* Category */}
                                                {job.category && (
                                                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                                        {typeof job.category === 'string' ? job.category : job.category?.name}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Job Description */}
                                            <div
                                                className="text-gray-600 mb-4 leading-relaxed prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{
                                                    __html: job.description || (job.duties && job.duties.length > 200
                                                        ? `${job.duties.substring(0, 200)}...`
                                                        : job.duties)
                                                }}
                                            />

                                            {/* Expandable Details */}
                                            {isExpanded && (
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    {/* Requirements */}
                                                    {job.requirements && (
                                                        <div className="mb-4">
                                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Requirements</h3>
                                                            <div
                                                                className="text-gray-700 prose prose-sm max-w-none"
                                                                dangerouslySetInnerHTML={{ __html: job.requirements }}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Full Duties */}
                                                    {job.duties && (
                                                        <div className="mb-4">
                                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Job Description</h3>
                                                            <div
                                                                className="text-gray-700 prose prose-sm max-w-none"
                                                                dangerouslySetInnerHTML={{ __html: job.duties }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* View Details Button */}
                                            <button
                                                onClick={() => setExpandedJobId(isExpanded ? null : jobKey)}
                                                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1 mt-2"
                                            >
                                                {isExpanded ? (
                                                    <>
                                                        <span>Show less</span>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>View details</span>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </>
                                                )}
                                            </button>

                                            {/* Location */}
                                            <div className="flex items-center text-gray-500 text-sm mt-3">
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {job.location}
                                            </div>
                                        </div>

                                        {/* Apply Button */}
                                        <button
                                            onClick={() => {
                                                setSelectedJob(job);
                                                setIsApplicationModalOpen(true);
                                            }}
                                            className={`w-full md:w-auto md:flex-shrink-0 font-semibold px-6 py-2.5 rounded-lg transition text-sm ${
                                                hasAppliedToJob(job.id)
                                                    ? 'bg-gray-300 text-gray-600 cursor-default'
                                                    : 'bg-primary text-white hover:bg-[#144E73]'
                                            }`}
                                        >
                                            {hasAppliedToJob(job.id) ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Applied
                                                </span>
                                            ) : (
                                                'Apply Now'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {lastPage > 1 && (
                        <div className="mt-8 flex justify-center items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                Previous
                            </button>

                            <div className="flex gap-1">
                                {[...Array(lastPage)].map((_, idx) => {
                                    const pageNum = idx + 1;
                                    // Show first page, last page, current page, and pages around current
                                    if (
                                        pageNum === 1 ||
                                        pageNum === lastPage ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                                    currentPage === pageNum
                                                        ? 'bg-primary text-white'
                                                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (
                                        pageNum === currentPage - 2 ||
                                        pageNum === currentPage + 2
                                    ) {
                                        return <span key={pageNum} className="px-2 py-2 text-gray-500">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === lastPage}
                                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                Next
                            </button>
                        </div>
                    )}
                    </>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No jobs match your filters.</p>
                    </div>
                )}

                {/* Application Modal */}
                <ApplicationModal
                    isOpen={isApplicationModalOpen}
                    onClose={() => {
                        setIsApplicationModalOpen(false);
                        setSelectedJob(null);
                    }}
                    job={selectedJob}
                />
            </div>
        </div>
    );
};

export default Careers;
